import { Fragment } from "react";
import { AlertCircle } from "lucide-react";
import type { RequiredField } from "@/hooks/useRequiredFields";
import type { FriendlyError } from "@/lib/apiErrorMessages";

/** Red text under a field. Renders nothing without a message. */
export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 flex items-center gap-1 text-xs text-destructive">
      <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden /> {message}
    </p>
  );
}

/** «Не заповнено: Клієнт · Короткий опис» above the submit button; each item jumps to its field. */
export function MissingFieldsSummary({
  missing,
  onJump,
}: {
  missing: RequiredField[];
  onJump: (key: string) => void;
}) {
  if (missing.length === 0) return null;
  return (
    <div
      role="alert"
      className="mb-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
    >
      <span className="font-medium">Не заповнено:</span>{" "}
      {missing.map((f, i) => (
        <Fragment key={f.key}>
          {i > 0 && " · "}
          <button
            type="button"
            onClick={() => onJump(f.key)}
            className="underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {f.label}
          </button>
        </Fragment>
      ))}
    </div>
  );
}

/** Server error (already mapped to Ukrainian) + small technical code for unknown ones. */
export function SubmitErrorNotice({ error }: { error: FriendlyError | null }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      className="mb-2 flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
    >
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <div>
        <p>{error.message}</p>
        {error.code && <p className="mt-0.5 text-[10px] text-muted-foreground">Код: {error.code}</p>}
      </div>
    </div>
  );
}
